/**
 * 班级「课堂记录 / 考勤」(博颂 2026-09-24,替代校宝的每堂课记录与考勤)。
 * 选日期 + 节次(到校 / 第 n 节)→ 一张点名册:出勤、课堂表现 1–5、一句备注。
 * 迟到/缺勤/早退按园长设的规则自动记入行规分(保存后提示条数)。
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { GradeItemSelect } from "@/components/GradeItemSelect";
import { Link, useParams } from "react-router-dom";

import {
  ApiError,
  ATTENDANCE,
  useAttendanceRules,
  useDeleteLesson,
  useLessonSheet,
  useLessonSubjects,
  useRecentLessons,
  useSaveLessonSheet,
  type Attendance,
  type LessonSheet,
  type SheetRow,
} from "../../api/lessons";
import { canManageSchool, useClassWithRoster, useOrgMe } from "../../api/school";
import { LeavePanel } from "./LeavePanel";
import { useMyEmployees } from "../workbench/EmployeeSwitcher";

const ATT_CLS: Record<Attendance, string> = {
  present: "bg-emerald-50 text-emerald-700 border-emerald-200",
  late: "bg-amber-50 text-amber-700 border-amber-200",
  absent: "bg-rose-50 text-rose-700 border-rose-200",
  leave: "bg-sky-50 text-sky-700 border-sky-200",
  early: "bg-orange-50 text-orange-700 border-orange-200",
};

const today = () => new Date().toISOString().slice(0, 10);

export function ClassLessonsPage(): JSX.Element {
  const { t } = useTranslation();
  const { classId = "" } = useParams();
  const klass = useClassWithRoster(classId);
  const [date, setDate] = useState(today());
  const [period, setPeriod] = useState(1);
  // 这节课的学科:选项 = 本行政班当前学期的课程(博颂 2026-10-06:学科管理里新建、还没有
  // 成员的学科也要能选)。没拆课程的旧班级退回到「我的学科」。分层课(名单是子集)的点名册
  // 记在课程上:课程自己的名单、每节一张,同一节并行的几个分层互不覆盖。
  const { employees: myEmployees } = useMyEmployees();
  const subjectsQ = useLessonSubjects(classId);
  const courseSubjects = subjectsQ.data ?? [];
  const options: { slug: string; name: string }[] = courseSubjects.length ? courseSubjects : myEmployees;
  // pickedSubject = 老师自己选的(下拉 / 点下方记录);savedSubject = 已保存这一节记的学科,
  // 只管这一节。博颂 2026-10-13:原来打开别人点过的节会把那节的学科写进 pickedSubject,
  // 切到自己那节还沿用(李婷婷的历史课记成了综合英语);只有一个选项时下拉又被隐藏,改不了。
  const [pickedSubject, setPickedSubject] = useState<string>("");
  const [savedSubject, setSavedSubject] = useState<{ slug: string; name: string } | null>(null);
  const mySlugs = new Set(myEmployees.map((e: { slug: string }) => e.slug));
  // 2026-10-14:没点过的节只在没有歧义时自动带学科(我教的课里恰好一门,或总共只有一门);
  // 否则留空、必须先选 —— 以前默认第一个,多学科老师 / 班主任不改就记错学科(张吟那节记成数学)。
  const mineOpts = options.filter((o) => mySlugs.has(o.slug));
  const defaultSubject = (mineOpts.length === 1 ? mineOpts[0]?.slug : options.length === 1 ? options[0]?.slug : "") ?? "";
  const choice = options.some((o) => o.slug === pickedSubject) ? pickedSubject : defaultSubject;
  const tier = courseSubjects.find((c) => c.slug === choice && c.partial) ?? null;
  const subject = savedSubject?.slug ?? choice;
  // 已保存的学科不在我的选项里(别人点的、或以前记错的)→ 也列出来,老师看得到、能改
  const subjectOptions = savedSubject && !options.some((o) => o.slug === savedSubject.slug) ? [...options, savedSubject] : options;
  const needSubject = options.length > 0 && !subject;
  const sheetClassId = tier ? tier.course_id : classId;
  const sheet = useLessonSheet(sheetClassId, date, period);
  // 下方列表 = 整个行政班的点名(含分层课,博颂 2026-10-10),默认只看所选日期那天
  const [showOlder, setShowOlder] = useState(false);
  const recent = useRecentLessons(classId, showOlder ? null : date);
  const rules = useAttendanceRules();
  const save = useSaveLessonSheet(sheetClassId);
  const del = useDeleteLesson(sheetClassId);
  const [rows, setRows] = useState<SheetRow[]>([]);
  const [topic, setTopic] = useState("");
  // 048:课堂表现星级归入成绩体系里的课堂类评估项(可选)
  const [gradeItem, setGradeItem] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // 请假登记(054):班主任 / 校长 / 主任可登记,其他老师只看
  const orgMe = useOrgMe();
  const canRegisterLeave = canManageSchool(orgMe.data) || (orgMe.data?.homeroom_class_ids ?? []).includes(classId);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 乐观并发:读到的点名册版本(updated_at;新点名册为 null),保存时带上;
  // 别人期间保存过 → 409,conflict 存提示,老师的未保存修改留在页面上
  const [version, setVersion] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // 重复点名(博颂 2026-10-13):整班点名册里,已在同节分层课点过的学生默认不点
  const [includeOverlap, setIncludeOverlap] = useState(false);
  const isCourse = !!sheet.data?.is_course;
  const overlapRows = rows.filter((r) => r.elsewhere);
  const skipped = !isCourse && !includeOverlap ? new Set(overlapRows.map((r) => r.student_id)) : new Set<string>();
  const visibleRows = skipped.size ? rows.filter((r) => !skipped.has(r.student_id)) : rows;

  function applySheet(d: LessonSheet) {
    setRows(d.students);
    setTopic(d.topic ?? "");
    setGradeItem(d.grade_item ?? null);
    setSavedSubject(d.subject ? { slug: d.subject, name: d.subject_name ?? d.subject } : null);
    setVersion(d.updated_at ?? null);
  }

  // 换日期/节次/课程:清提示,也清掉上一节的点名数据 —— 新一节还没加载完(或加载失败)时
  // 点保存,会把上一节的出勤、主题、学科写进这一节(2026-10-13 review)。
  // 必须放在 applySheet 那个 effect 前面:新一节已在缓存里时两个 effect 同一轮执行,先清后填。
  // 保存后重新拉点名册不触发这里,不会把「已保存」冲掉。
  useEffect(() => {
    setMsg(null); setErr(null); setConflict(null); setIncludeOverlap(false);
    setRows([]); setTopic(""); setGradeItem(null); setSavedSubject(null); setVersion(null);
  }, [date, period, sheetClassId]);
  useEffect(() => {
    if (sheet.data) applySheet(sheet.data);
  }, [sheet.data]);
  const sheetReady = !!sheet.data && !sheet.isError;

  async function reloadAfterConflict() {
    const r = await sheet.refetch();
    if (r.data) applySheet(r.data);
    setConflict(null);
    setErr(null);
  }

  const counts = useMemo(() => {
    const c: Record<Attendance, number> = { present: 0, late: 0, absent: 0, leave: 0, early: 0 };
    visibleRows.forEach((r) => (c[r.attendance] += 1));
    return c;
  }, [visibleRows]);

  const patch = (id: string, p: Partial<SheetRow>) => setRows((rs) => rs.map((r) => (r.student_id === id ? { ...r, ...p } : r)));

  async function onSave() {
    if (needSubject) return;
    setErr(null);
    setMsg(null);
    setConflict(null);
    try {
      const out = await save.mutateAsync({
        date,
        period,
        topic: topic.trim() || null,
        grade_item: gradeItem,
        subject: subject || null,
        expected_updated_at: version,
        marks: visibleRows.map(({ student_id, attendance, performance, note }) => ({ student_id, attendance, performance, note })),
        excluded: [...skipped],
      });
      // 立刻换成新版本号:重新拉点名册之前再点一次保存也不会误报冲突
      setVersion(out.updated_at);
      setMsg(t("lessons.saved", { n: out.saved, c: out.conduct_changes }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflict(e.message);
      else setErr((e as Error).message);
    }
  }

  return (
    <div className="pb-24 md:pb-0">
      <Link to={`/classes/${classId}`} className="text-sm text-sky-600 hover:underline">{t("gpa.grades.back")}</Link>
      <header className="mb-4 mt-1">
        <h1 className="text-xl font-semibold text-slate-800 md:text-2xl">{klass.data?.name ?? ""} · {t("lessons.title")}</h1>
        <p className="hidden text-sm text-slate-500 md:block">
          {t("lessons.hint")}
          {rules.data && (
            <span className="ml-1 text-slate-400">
              {t("lessons.rules", { late: rules.data.late, absent: rules.data.absent, early: rules.data.early })}
            </span>
          )}
        </p>
      </header>

      <div className="mb-2 flex justify-end">
        <button type="button" onClick={() => setLeaveOpen(true)} className="rounded border border-sky-300 bg-white px-3 py-1 text-xs text-sky-700 hover:bg-sky-50" data-testid="leave-open">
          {t("lessons.leave.button")}{rows.some((r) => r.registered_leave) ? ` · ${t("lessons.leave.today", { n: rows.filter((r) => r.registered_leave).length })}` : ""}
        </button>
      </div>
      {leaveOpen && (
        <LeavePanel homeroomId={classId} canEdit={canRegisterLeave} onClose={() => setLeaveOpen(false)}
          students={(klass.data?.students ?? []).map((s: { id: string; name_zh: string; name_en?: string | null }) => ({ id: s.id, name_zh: s.name_zh, name_en: s.name_en }))} />
      )}
      {tier && (
        <p className="mb-2 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="lesson-tier-note">
          {t("lessons.tierNote", { course: tier.course_name, n: tier.students })}
        </p>
      )}
      <section className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
        <label className="text-xs text-slate-600">
          {t("lessons.date")}
          <br />
          <input id="lesson-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded border border-slate-200 px-2 py-1" />
        </label>
        <label className="text-xs text-slate-600">
          {t("lessons.period")}
          <br />
          <select id="lesson-period" value={period} onChange={(e) => setPeriod(Number(e.target.value))} className="rounded border border-slate-200 px-2 py-1">
            <option value={0}>{t("lessons.arrival")}</option>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
              <option key={p} value={p}>{t("lessons.nth", { n: p })}</option>
            ))}
          </select>
        </label>
        <label className="min-w-[14rem] flex-1 text-xs text-slate-600">
          {t("lessons.topic")}
          <br />
          <input id="lesson-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("lessons.topicPh")} className="w-full rounded border border-slate-200 px-2 py-1" />
        </label>
        {subjectOptions.length > 0 && (
          <label className="text-xs text-slate-600">
            {t("exams.create.subjectLabel")}
            <br />
            <select id="lesson-subject" value={subject} onChange={(e) => {
              const v = e.target.value;
              if (!options.some((o) => o.slug === v)) return;
              setPickedSubject(v); setSavedSubject(null); setGradeItem(null);
            }} className={`rounded border px-2 py-1 ${needSubject ? "border-amber-400" : "border-slate-200"}`} data-testid="lesson-subject">
              {!subject && <option value="">{t("exams.create.subjectPick")}</option>}
              {subjectOptions.map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
            </select>
            {needSubject && <span className="ml-1 text-amber-700">{t("exams.create.subjectRequired")}</span>}
          </label>
        )}
        <label className="text-xs text-slate-600">
          {t("gradeItem.starsLabel")}
          <br />
          <GradeItemSelect classId={classId} subject={subject || null} category="classroom" optional value={gradeItem} onChange={(it) => setGradeItem(it?.key ?? null)}
            className="rounded border border-slate-200 px-2 py-1" fallback={<span className="text-slate-400">{t("gradeItem.noneConfigured")}</span>} />
        </label>
        <button type="button" onClick={() => setRows((rs) => rs.map((r) => (r.registered_leave ? r : { ...r, attendance: "present" })))} title={t("lessons.leave.allPresentHint")} className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">
          {t("lessons.allPresent")}
        </button>
        <button type="button" onClick={() => void onSave()} disabled={save.isPending || rows.length === 0 || !sheetReady || needSubject} className="hidden rounded bg-sky-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-600 disabled:opacity-50 md:inline-block" data-testid="lesson-save">
          {save.isPending ? t("common.saving") : t("common.save")}
        </button>
      </section>

      <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {ATTENDANCE.map((a) => (
          <span key={a}>{t(`lessons.att.${a}`)} <b className="tabular-nums text-slate-700">{counts[a]}</b></span>
        ))}
        {sheet.data?.saved && (
          <span className="text-slate-400">· {t("lessons.lastSaved", { who: sheet.data.teacher_name ?? "—", at: new Date(sheet.data.updated_at ?? "").toLocaleString() })}</span>
        )}
        {!sheet.data?.saved && sheet.data && <span className="text-amber-600">· {t("lessons.notSaved")}</span>}
      </div>
      {msg && <p className="mb-2 text-sm text-emerald-700">{msg}</p>}
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
      {conflict && (
        <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" role="alert" data-testid="lesson-conflict">
          <p className="font-medium">{conflict}</p>
          <p className="mt-1 text-xs text-amber-700">{t("lessons.conflictHint")}</p>
          <button type="button" onClick={() => void reloadAfterConflict()} disabled={sheet.isFetching}
            className="mt-2 rounded border border-amber-400 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50">
            {t("lessons.conflictReload")}
          </button>
        </div>
      )}

      {overlapRows.length > 0 && (
        <OverlapNotice rows={overlapRows} isCourse={isCourse} include={includeOverlap} onToggle={() => setIncludeOverlap((v) => !v)} />
      )}

      {/* 手机:每人一张卡片,大按钮;备注点 ✎ 展开 */}
      <ul className="flex flex-col gap-2 md:hidden" data-testid="lesson-cards">
        {visibleRows.map((r) => (
          <MobileMarkCard key={r.student_id} row={r} onPatch={(p) => patch(r.student_id, p)} />
        ))}
      </ul>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white md:block">
        <table className="w-full text-sm" data-testid="lesson-sheet">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">{t("gpa.grades.col.name")}</th>
              <th className="px-3 py-2 text-left">{t("lessons.attendance")}</th>
              <th className="px-3 py-2 text-left">{t("lessons.performance")}</th>
              <th className="px-3 py-2 text-left">{t("lessons.note")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.student_id} className="border-t border-slate-100 align-middle">
                <td className="whitespace-nowrap px-3 py-1.5">
                  <span className="font-medium text-slate-800">{r.name_zh}</span>
                  {r.name_en && <span className="ml-1 text-xs text-slate-400">{r.name_en}</span>}
                  <LeaveChip row={r} />
                  <ElsewhereChip row={r} />
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex gap-1" role="radiogroup" aria-label={t("lessons.attendance")}>
                    {ATTENDANCE.map((a) => (
                      <button
                        key={a}
                        type="button"
                        role="radio"
                        aria-checked={r.attendance === a}
                        onClick={() => patch(r.student_id, { attendance: a })}
                        className={`rounded border px-2 py-0.5 text-xs ${r.attendance === a ? ATT_CLS[a] + " font-medium" : "border-slate-200 text-slate-400 hover:text-slate-600"}`}
                      >
                        {t(`lessons.attShort.${a}`)}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex gap-0.5" role="radiogroup" aria-label={t("lessons.performance")}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        aria-label={`${n}`}
                        onClick={() => patch(r.student_id, { performance: r.performance === n ? null : n })}
                        className={`h-6 w-6 rounded text-sm ${r.performance != null && n <= r.performance ? "text-amber-500" : "text-slate-300 hover:text-slate-400"}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <input
                    id={`note-${r.student_id}`}
                    value={r.note ?? ""}
                    onChange={(e) => patch(r.student_id, { note: e.target.value })}
                    placeholder={t("lessons.notePh")}
                    className="w-full min-w-[12rem] rounded border border-slate-200 px-2 py-1 text-sm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !sheet.isLoading && <p className="p-4 text-sm text-slate-500">{t("gpa.grades.noStudents")}</p>}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div className="min-w-0 flex-1 text-xs text-slate-600">
          <span className="font-medium text-slate-800">{t("lessons.att.present")} {counts.present}</span>
          {counts.late + counts.absent + counts.leave + counts.early > 0 && (
            <span className="ml-2 text-amber-700">
              {(["late", "absent", "leave", "early"] as const).filter((a) => counts[a]).map((a) => `${t(`lessons.att.${a}`)} ${counts[a]}`).join(" · ")}
            </span>
          )}
          {msg && <div className="truncate text-emerald-700">{msg}</div>}
          {err && <div className="truncate text-rose-600">{err}</div>}
          {conflict && <div className="truncate text-amber-700">{t("lessons.conflictShort")}</div>}
        </div>
        <button type="button" onClick={() => void onSave()} disabled={save.isPending || rows.length === 0 || !sheetReady || needSubject}
          className="rounded-lg bg-sky-500 px-6 py-2.5 text-base font-medium text-white active:bg-sky-600 disabled:opacity-50" data-testid="lesson-save-mobile">
          {save.isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>

      <div className="mb-2 mt-6 flex items-baseline gap-3">
        <h2 className="text-sm font-semibold text-slate-700">{showOlder ? t("lessons.recent") : t("lessons.dayRecords", { date })}</h2>
        <button type="button" className="text-xs text-sky-600 hover:underline" onClick={() => setShowOlder((v) => !v)} data-testid="lessons-toggle-older">
          {showOlder ? t("lessons.onlyDay") : t("lessons.showOlder")}
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <tbody>
            {(recent.data ?? []).map((l) => {
              const current = l.date === date && l.period === period && (l.course_id ? l.course_id === tier?.course_id : !tier);
              return (
              <tr key={l.id} className={`border-t border-slate-100 first:border-t-0 ${current ? "bg-sky-50" : ""}`} data-testid="lesson-recent-row">
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <button type="button" className="text-sky-600 hover:underline" onClick={() => {
                    setDate(l.date); setPeriod(l.period);
                    if (l.subject && options.some((o) => o.slug === l.subject)) setPickedSubject(l.subject);
                  }}>
                    {showOlder ? `${l.date} · ` : ""}{l.period === 0 ? t("lessons.arrival") : t("lessons.nth", { n: l.period })}
                  </button>
                </td>
                <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">
                  {l.subject_name ?? ""}
                  {l.course_id && <span className="ml-1 text-slate-400">{t("lessons.tierCount", { n: l.students })}</span>}
                </td>
                <td className="px-3 py-1.5 text-slate-600">{l.topic ?? ""}</td>
                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">
                  {t("lessons.recentCounts", { late: l.late, absent: l.absent, leave: l.leave, early: l.early })}
                </td>
                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">
                  {l.avg_performance != null ? `★ ${l.avg_performance}` : ""} {l.notes ? t("lessons.nNotes", { n: l.notes }) : ""}
                </td>
                <td className="px-3 py-1.5 text-slate-400">{l.teacher_name ?? ""}</td>
                <td className="px-3 py-1.5 text-right">
                  <button type="button" onClick={() => { if (window.confirm(t("lessons.deleteConfirm"))) del.mutate(l.id); }} className="text-rose-500 hover:underline">
                    {t("common.delete")}
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {recent.data?.length === 0 && <p className="p-3 text-xs text-slate-400">{showOlder ? t("lessons.noRecent") : t("lessons.noDayRecords")}</p>}
      </div>
    </div>
  );
}

function MobileMarkCard({ row, onPatch }: { row: SheetRow; onPatch: (p: Partial<SheetRow>) => void }) {
  const { t } = useTranslation();
  const [noteOpen, setNoteOpen] = useState(!!row.note);
  return (
    <li className={`rounded-lg border bg-white p-3 ${row.attendance === "present" ? "border-slate-200" : "border-amber-300"}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate">
          <span className="text-base font-medium text-slate-800">{row.name_zh}</span>
          {row.name_en && <span className="ml-1 text-xs text-slate-400">{row.name_en}</span>}
          <LeaveChip row={row} />
          <ElsewhereChip row={row} />
        </span>
        <button type="button" onClick={() => setNoteOpen((o) => !o)} className={`shrink-0 rounded px-2 py-1 text-sm ${row.note ? "text-sky-600" : "text-slate-400"}`} aria-label={t("lessons.note")}>
          ✎
        </button>
      </div>
      <div className="mt-2 grid grid-cols-5 gap-1.5" role="radiogroup" aria-label={t("lessons.attendance")}>
        {ATTENDANCE.map((a) => (
          <button key={a} type="button" role="radio" aria-checked={row.attendance === a} onClick={() => onPatch({ attendance: a })}
            className={`h-10 rounded-md border text-sm ${row.attendance === a ? ATT_CLS[a] + " font-semibold" : "border-slate-200 text-slate-500 active:bg-slate-50"}`}>
            {t(`lessons.att.${a}`)}
          </button>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-1" role="radiogroup" aria-label={t("lessons.performance")}>
        <span className="mr-1 text-xs text-slate-400">{t("lessons.performance")}</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" aria-label={`${n}`} onClick={() => onPatch({ performance: row.performance === n ? null : n })}
            className={`h-9 w-9 rounded text-xl ${row.performance != null && n <= row.performance ? "text-amber-500" : "text-slate-300"}`}>
            ★
          </button>
        ))}
      </div>
      {noteOpen && (
        <input id={`m-note-${row.student_id}`} value={row.note ?? ""} onChange={(e) => onPatch({ note: e.target.value })} placeholder={t("lessons.notePh")}
          className="mt-2 w-full rounded border border-slate-200 px-2 py-2 text-base" />
      )}
    </li>
  );
}

/** 班主任登记了请假的学生:名字旁一个小标签;老师改成别的出勤时提示「已改」。 */
function LeaveChip({ row }: { row: SheetRow }) {
  const { t } = useTranslation();
  const lv = row.registered_leave;
  if (!lv) return null;
  const span = lv.start_date === lv.end_date ? lv.start_date : `${lv.start_date} ~ ${lv.end_date}`;
  const title = [`${lv.label} ${span}`, lv.note, lv.by ? t("lessons.leave.by", { name: lv.by }) : null].filter(Boolean).join(" · ");
  return (
    <span title={title} data-testid="leave-chip" className={`ml-2 rounded px-1.5 py-0.5 text-[11px] ${row.attendance === "leave" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}>
      {row.attendance === "leave" ? t("lessons.leave.chip", { label: lv.label }) : t("lessons.leave.chipChanged", { label: lv.label })}
    </span>
  );
}

/** 同节已在别的点名册里点过:按点名册分组列出;整班点名册默认不点这些学生,可切换。 */
function OverlapNotice({ rows, isCourse, include, onToggle }: { rows: SheetRow[]; isCourse: boolean; include: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const groups = new Map<string, { label: string; teacher: string | null; names: string[] }>();
  rows.forEach((r) => {
    const e = r.elsewhere!;
    const g = groups.get(e.lesson_id) ?? { label: e.label, teacher: e.teacher_name, names: [] };
    g.names.push(r.name_zh);
    groups.set(e.lesson_id, g);
  });
  const list = [...groups.values()].map((g) => t("lessons.overlap.group", { label: g.label, teacher: g.teacher ?? "—", n: g.names.length })).join("、");
  return (
    <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" role="status" data-testid="lesson-overlap">
      <p className="font-medium">{t("lessons.overlap.title", { list })}</p>
      <p className="mt-1 text-xs text-amber-700">
        {isCourse ? t("lessons.overlap.courseHint") : include ? t("lessons.overlap.includedHint") : t("lessons.overlap.skippedHint", { names: rows.map((r) => r.name_zh).join("、") })}
      </p>
      {!isCourse && (
        <button type="button" onClick={onToggle} data-testid="lesson-overlap-toggle"
          className="mt-2 rounded border border-amber-400 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100">
          {include ? t("lessons.overlap.skip") : t("lessons.overlap.include")}
        </button>
      )}
    </div>
  );
}

function ElsewhereChip({ row }: { row: SheetRow }) {
  const { t } = useTranslation();
  const e = row.elsewhere;
  if (!e) return null;
  return (
    <span data-testid="elsewhere-chip" className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">
      {t("lessons.overlap.chip", { label: e.label, teacher: e.teacher_name ?? "—" })}
    </span>
  );
}
